import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { XMarkIcon } from "@heroicons/react/24/outline";
import ImageNotFound from "../../images/imagenotfound.jpg";

const TutorialModal = ({ steps, isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (zoomed) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [zoomed]);

  if (!isOpen) return null;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setCurrentStep(0);
      onClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const current = steps[currentStep];

  return (
    <>
      <dialog className="modal modal-open text-onWhite z-50">
        <div className="modal-box w-full max-w-3xl max-h-[90vh] overflow-auto rounded-lg p-8">
          {/* Titre */}
          <h3 className="font-bold text-xl text-center">
            {current.showtitle && current.title}
          </h3>

          <button
            className="absolute top-0 right-0 text-black hover:text-gray-300 p-2"
            onClick={() => onClose()}
            aria-label="Fermer l’image agrandie"
          >
            <XMarkIcon className="w-10 h-10" />
          </button>

          {/* Sous-titre (si présent) */}
          {current.subtitle && (
            <p className="text-sm text-center mt-2 text-gray-400">
              {current.showtitle && current.subtitle}
            </p>
          )}

          <div className="py-4 space-y-4">
            {/* Image cliquable */}
            {current.image && (
              <img
                src={current.image}
                onClick={() => setZoomed(true)}
                alt={current.alt || "Étape d'introduction"}
                loading="lazy"
                decoding="async"
                width="800"
                fetchpriority="high"
                height="450"
                className={`w-full ${
                  current.isImageOnly
                    ? "max-h-full"
                    : "max-h-[300px] sm:max-h-[400px]"
                } object-contain cursor-zoom-in rounded ${
                  current.isImageOnly ? "" : "shadow-xl"
                }`}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = ImageNotFound;
                }}
              />
            )}

            {/* Texte principal */}
            {current.content && <p className="px-2">{current.content}</p>}

            {/* Liste de tips (si présents) */}
            {current.tips && current.tips.length > 0 && (
              <ul className="list-disc pl-6 space-y-1 text-sm">
                {current.tips.map((tip, index) => (
                  <li key={index}>{tip}</li>
                ))}
              </ul>
            )}
          </div>

          {/* Points d'étapes */}
          {steps.length > 1 && (
            <div className="flex justify-center mt-4 gap-2">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`w-3 h-3 rounded-full ${
                    index === currentStep ? "bg-primary" : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          )}

          {/* Boutons navigation */}
          <div className="modal-action justify-between">
            {currentStep > 0 ? (
              <button
                className="rounded-full bg-buttonHoverCyan border-borderCyan border px-4 py-2 text-onWhite"
                onClick={handlePrev}
              >
                {t("calendar.previous")}
              </button>
            ) : (
              <div />
            )}
            {steps.length >= 1 && (
              <button
                className="rounded-full bg-primary border-primary border px-4 py-2 text-text"
                onClick={current?.primaryButton?.onClick || handleNext}
              >
                {currentStep === steps.length - 1 || steps.length === 1
                  ? t("modal.gettingStarted")
                  : t("stepOne.nextButton")}
              </button>
            )}
          </div>
        </div>
      </dialog>

      {/* Modal de zoom plein écran */}
      {zoomed && current.image && (
        <div
          className="fixed inset-0 z-[999] bg-black/90 flex items-center justify-center"
          onClick={() => setZoomed(false)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 p-6"
            onClick={() => setZoomed(false)}
            aria-label="Fermer l’image agrandie"
          >
            <XMarkIcon className="w-10 h-10" />
          </button>
          <img
            src={current.image}
            alt={current.alt || "Zoom de l’étape"}
            loading="lazy"
            decoding="async"
            width="800"
            height="450"
            className="w-full h-full object-contain max-w-screen max-h-screen p-12"
          />
        </div>
      )}
    </>
  );
};

export default TutorialModal;
